import uniqBy from 'lodash/uniqBy'

import {
  ConversationRowContent,
  ConversationRowParty,
  NewConversation,
  Pagination,
  RequestForQuote,
  User,
} from '../../types'

import useGqlClient from '../hooks/useGqlClient'
import {
  FetchConversationsDocument,
  FetchMessagesForConversationDocument,
  useCreateConversationMutation,
  useCreateMessageMutation,
  useFetchMessagesForConversationQuery,
  useReadReceiptsForConversationQuery,
  RequestForQuoteStep,
} from '@/suppliers/_gen/gql'

import InboxCommon, { Conversation } from '@/gf/components/Inbox'
import InboxDetails from './InboxDetails'
import ConversationM from '@/gf/modules/Conversation'
import SuppliersConversationM from '../modules/Conversation'
import BuyersConversationM from '@/buyers/modules/Conversation'

const conversationRowContent = (
  conversation: Conversation | NewConversation
): ConversationRowContent => {
  const { requestForQuote, storeOrder, store, admin, name } = conversation
  let title: ConversationRowParty
  let description: ConversationRowParty
  if (!admin) {
    title = store
      ? {
          type: 'supplier',
          name: store ? BuyersConversationM.conversationName(store) : 'Error - undefined Store',
        }
      : { type: 'internal_org', name: '(Internal)' }
    description = {
      type: 'buyer',
      name: requestForQuote
        ? SuppliersConversationM.conversationNameForRfq(requestForQuote, true)
        : 'Error - undefined RFQ',
    }
  } else {
    title = { type: 'admin', name }
    description = store
      ? { type: 'supplier', name: BuyersConversationM.conversationName(store) }
      : requestForQuote
        ? {
            type: 'buyer',
            name: SuppliersConversationM.conversationNameForRfq(requestForQuote, true),
          }
        : storeOrder && 'order' in storeOrder
          ? {
              type: 'buyer',
              name: SuppliersConversationM.conversationNameForStoreOrderCommon(storeOrder),
            }
          : { type: 'buyer', name: 'Error - undefined RFQ, StoreOrder, and Store' }
  }
  return { name: title, details: description }
}

const AdminRequestForQuoteInbox = ({
  conversations,
  refetchConversations,
  page,
  setPage,
  pagination,
  user,
  requestForQuote,
  selectedConversationId,
  onSelectedConversationIdChange,
}: {
  conversations: Conversation[]
  refetchConversations: () => Promise<unknown>
  page: number
  setPage: (page: number) => void
  pagination: Pagination
  user: Pick<User, 'id' | 'role'>
  requestForQuote: RequestForQuote & { step: RequestForQuoteStep }
  selectedConversationId: string | undefined
  onSelectedConversationIdChange: (conversationId: string | undefined) => void
}) => {
  const gqlClient = useGqlClient()
  const [createMessage] = useCreateMessageMutation({
    client: gqlClient,
    refetchQueries: [FetchMessagesForConversationDocument, FetchConversationsDocument],
  })
  const [createConversation] = useCreateConversationMutation({
    client: gqlClient,
    refetchQueries: [FetchConversationsDocument],
  })

  const possibleConversationStores = uniqBy(
    [
      ...requestForQuote.storeOrders.map((storeOrder) => storeOrder.store),
      ...requestForQuote.requestForQuoteStores.map((rfqStore) => rfqStore.store),
      // Invited vendors (including declined ones) so support can always reach them
      ...(requestForQuote.requestForQuoteVendors ?? []).flatMap((rfqVendor) =>
        rfqVendor.vendor?.store ? [rfqVendor.vendor.store] : []
      ),
    ],
    (store) => store.id
  )
  const newConversationsUnfiltered = [
    // Non-Admin conversations
    ...possibleConversationStores.map((store) =>
      SuppliersConversationM.newNonAdminConversationRfq(requestForQuote, store, true)
    ),
    // Admin conversations
    BuyersConversationM.newAdminConversationRfq(
      requestForQuote.user?.organization?.id,
      requestForQuote
    ),
    ...possibleConversationStores.map((store) =>
      SuppliersConversationM.newAdminConversationRfq(requestForQuote, store)
    ),
  ]
  // Filter out existing conversations
  const newConversations = ConversationM.filterNewConversations(
    newConversationsUnfiltered,
    conversations
  )

  const selectedConversation =
    conversations &&
    [...conversations, ...newConversations].find((c) => c.id === selectedConversationId)

  const { error: messagesError, ...messagesQueryResult } = useFetchMessagesForConversationQuery({
    client: gqlClient,
    variables: {
      conversationId: selectedConversation?.id as string,
      vendorId: null,
      vendorUserId: null,
    },
    pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
    // Only refetch if the conversation is unread, so we can fetch the correct "read" status
    onCompleted: selectedConversation?.unreadMessages ? refetchConversations : undefined,
    skip:
      !selectedConversationId || !selectedConversation || 'newConversation' in selectedConversation,
  })

  const { fetchMessagesForConversation: messages } = messagesQueryResult.data || {}

  const { readReceiptsForConversation } =
    useReadReceiptsForConversationQuery({
      client: gqlClient,
      variables: {
        conversationId: selectedConversation?.id as string,
        vendorId: null,
        vendorUserId: null,
      },
      pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
      skip:
        !selectedConversationId ||
        !selectedConversation ||
        'newConversation' in selectedConversation,
    }).data || {}

  return (
    <InboxCommon
      className="mt-4"
      user={user}
      conversations={conversations}
      selectedConversation={selectedConversation}
      selectedConversationId={selectedConversationId}
      messages={messages}
      messagesError={messagesError}
      readReceiptsForConversation={readReceiptsForConversation}
      onSelectedConversationIdChange={onSelectedConversationIdChange}
      page={page}
      setPage={setPage}
      pagination={pagination}
      newConversations={newConversations}
      DetailsForConversation={InboxDetails.RequestForQuote(requestForQuote, '')}
      conversationRowContent={conversationRowContent}
      createMessage={(variables) =>
        createMessage({ variables: { ...variables, vendorId: null, vendorUserId: null } })
      }
      createConversation={(variables) =>
        createConversation({
          variables: { ...variables, vendorId: null, vendorUserId: null },
        }).then((resp) => resp.data?.createConversation)
      }
      readOnly={
        selectedConversation && ConversationM.isInternalOrgConversation(selectedConversation)
      }
    />
  )
}

export default AdminRequestForQuoteInbox
